import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Copy, ExternalLink, RefreshCw, Pencil, Check, X, Download } from "lucide-react";
import { useSite, useUrl } from "@/context/SiteContext";
import { useDepartments } from "@/context/DepartmentsContext";

/**
 * Admin panel — lists every active public page in the site with its full
 * URL, and lets you quickly edit slugs inline. Auto-updates when
 * departments or articles are added/modified.
 */
export default function SitemapPanel() {
  const { refresh: refreshContent } = useSite();
  const url = useUrl();
  const { allDepartments, loaded: deptsLoaded, refresh: refreshDepartments } = useDepartments();
  const [articles, setArticles] = useState(null);
  const [reloading, setReloading] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [editingId, setEditingId] = useState(null); // row.id currently being edited
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);
  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const loadArticles = async () => {
    try {
      const { data } = await api.get("/articles");
      setArticles(Array.isArray(data) ? data : []);
    } catch {
      setArticles([]);
    }
  };

  useEffect(() => { loadArticles(); }, []);

  const refresh = async () => {
    setReloading(true);
    await Promise.all([refreshDepartments(), loadArticles(), refreshContent()]);
    setReloading(false);
    toast.success("Listă URL-uri actualizată");
  };

  const regenerateSitemap = async () => {
    setRegenerating(true);
    try {
      const { data } = await api.post("/admin/sitemap/regenerate");
      toast.success(`sitemap.xml regenerat: ${data.url_count} URL-uri (${data.base})`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Nu s-a putut regenera sitemap.xml");
    } finally {
      setRegenerating(false);
    }
  };

  // Normalise a slug: lowercase, strip diacritics, kebab-case.
  const normalise = (s) =>
    (s || "")
      .toString()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .replace(/^\/+|\/+$/g, "")
      .replace(/\s+/g, "-")
      .replace(/[^a-z0-9-]/g, "")
      .replace(/-{2,}/g, "-");

  // Compose the full sitemap list from live data
  const groups = useMemo(() => {
    const depts = (allDepartments || []).slice().sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0) || (a.number || "").localeCompare(b.number || "")
    );
    const arts = (articles || []).slice().sort(
      (a, b) => (b.created_at || "").localeCompare(a.created_at || "")
    );

    const staticRows = [
      { id: "static-home", title: "Acasă", path: "/", slug: "", editable: false, note: "Rădăcină — slug fix" },
      { id: "static-departments", title: "Lista departamente", path: url.departments, slug: url.departments.slice(1), editable: true, kind: "content", contentKey: "page.departments.slug" },
      { id: "static-blog", title: "Blog (listă)", path: url.blog, slug: url.blog.slice(1), editable: true, kind: "content", contentKey: "page.blog.slug" },
      { id: "static-contact", title: "Contact", path: url.contact, slug: url.contact.slice(1), editable: true, kind: "content", contentKey: "page.contact.slug" },
    ];

    return [
      { id: "static", label: "Pagini statice", rows: staticRows },
      {
        id: "departments",
        label: `Pagini departament (${depts.length})`,
        rows: depts.map((d) => ({
          id: `dept-${d.id}`,
          title: `${d.number} — ${d.name}`,
          path: url.dept(d.slug),
          slug: d.slug,
          editable: true,
          kind: "department",
          recordId: d.id,
        })),
      },
      {
        id: "articles",
        label: articles === null ? "Articole blog (se încarcă...)" : `Articole blog (${arts.length})`,
        rows: arts.map((a) => ({
          id: `art-${a.id}`,
          title: a.title,
          path: url.article(a.slug),
          slug: a.slug,
          editable: true,
          kind: "article",
          recordId: a.id,
          draft: a.published === false,
        })),
      },
    ];
  }, [allDepartments, articles, url]);

  const totalPages = groups.reduce((sum, g) => sum + g.rows.length, 0);

  const copy = async (fullUrl) => {
    try {
      await navigator.clipboard.writeText(fullUrl);
      toast.success("URL copiat");
    } catch {
      toast.error("Nu am putut copia");
    }
  };

  const copyAll = async () => {
    const all = groups.flatMap((g) => g.rows.map((r) => `${origin}${r.path}`)).join("\n");
    try {
      await navigator.clipboard.writeText(all);
      toast.success(`${totalPages} URL-uri copiate`);
    } catch {
      toast.error("Nu am putut copia");
    }
  };

  const startEdit = (row) => {
    setEditingId(row.id);
    setDraft(row.slug);
  };
  const cancelEdit = () => {
    setEditingId(null);
    setDraft("");
  };

  const saveEdit = async (row) => {
    const cleaned = normalise(draft);
    if (!cleaned) {
      toast.error("Slug-ul nu poate fi gol");
      return;
    }
    if (cleaned === row.slug) {
      cancelEdit();
      return;
    }
    setSaving(true);
    try {
      if (row.kind === "content") {
        await api.post("/admin/site/content/bulk", { [row.contentKey]: cleaned });
        await refreshContent();
      } else if (row.kind === "department") {
        await api.put(`/admin/departments/${row.recordId}`, { slug: cleaned });
        await refreshDepartments();
      } else if (row.kind === "article") {
        await api.put(`/admin/articles/${row.recordId}`, { slug: cleaned });
        await loadArticles();
      }
      toast.success(`Slug actualizat: /${cleaned}`);
      cancelEdit();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Nu s-a putut salva slug-ul.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="sitemap-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="font-mono-label text-black/60">URL-uri / Sitemap</div>
          <div className="font-display font-medium uppercase text-2xl tracking-tight mt-1">
            {totalPages} pagini active
          </div>
          <div className="text-sm text-black/60 mt-1">
            Domeniu: <span className="font-mono-label">{origin}</span>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={copyAll}
            data-testid="sitemap-copy-all"
            className="font-mono-label border border-black px-4 py-2 hover:bg-black hover:text-white inline-flex items-center gap-2"
          >
            <Copy size={12} /> Copiază toate URL-urile
          </button>
          <button
            onClick={regenerateSitemap}
            disabled={regenerating}
            data-testid="sitemap-regenerate"
            className="font-mono-label border border-black px-4 py-2 hover:bg-black hover:text-white inline-flex items-center gap-2 disabled:opacity-50"
          >
            <Download size={12} className={regenerating ? "animate-spin" : ""} />
            {regenerating ? "Se regenerează..." : "Regenerează sitemap.xml"}
          </button>
          <button
            onClick={refresh}
            disabled={reloading}
            data-testid="sitemap-refresh"
            className="font-mono-label bg-black text-white px-4 py-2 border border-black hover:bg-white hover:text-black inline-flex items-center gap-2 disabled:opacity-50"
          >
            <RefreshCw size={12} className={reloading ? "animate-spin" : ""} />
            {reloading ? "Se actualizează..." : "Actualizează"}
          </button>
        </div>
      </div>

      <div className="space-y-8">
        {groups.map((g) => (
          <div key={g.id} data-testid={`sitemap-group-${g.id}`}>
            <div className="font-mono-label text-black/50 mb-2">{g.label}</div>
            {g.rows.length === 0 ? (
              <div className="border border-black p-6 text-center font-mono-label text-black/50">
                Nicio pagină în această categorie.
              </div>
            ) : (
              <div className="border border-black divide-y divide-black">
                {g.rows.map((r, i) => {
                  const full = `${origin}${r.path}`;
                  const isEditing = editingId === r.id;
                  return (
                    <div
                      key={r.id}
                      data-testid={`sitemap-row-${g.id}-${i}`}
                      className="p-4 flex items-center justify-between gap-4 hover:bg-black/5 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <div className="font-display font-bold uppercase text-sm truncate">
                          {r.title}
                        </div>

                        {isEditing ? (
                          <div className="mt-2 flex items-stretch gap-2" data-testid={`sitemap-edit-${g.id}-${i}`}>
                            <span className="font-mono-label text-black/60 flex items-center px-2 border border-black bg-black/5">
                              {r.kind === "department" ? `${url.departments}/` : r.kind === "article" ? `${url.blog}/` : "/"}
                            </span>
                            <input
                              autoFocus
                              value={draft}
                              onChange={(e) => setDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") saveEdit(r);
                                if (e.key === "Escape") cancelEdit();
                              }}
                              className="flex-1 bg-white border border-black px-3 py-1 text-sm font-mono-label focus:outline-none"
                              data-testid={`sitemap-slug-input-${g.id}-${i}`}
                              placeholder="slug-nou"
                            />
                          </div>
                        ) : (
                          <a
                            href={r.path}
                            target="_blank"
                            rel="noreferrer"
                            className="font-mono-label text-black/70 hover:text-black hover:underline block truncate mt-1 text-xs"
                            data-testid={`sitemap-url-${g.id}-${i}`}
                          >
                            {full}
                          </a>
                        )}

                        <div className="text-[10px] text-black/40 mt-1 font-mono-label">
                          {r.editable ? `Slug: /${r.slug}` : "Rădăcină — slug fix"}
                          {r.draft && " · draft"}
                        </div>
                      </div>

                      <div className="flex gap-2 shrink-0">
                        {isEditing ? (
                          <>
                            <button
                              onClick={() => saveEdit(r)}
                              disabled={saving}
                              data-testid={`sitemap-save-${g.id}-${i}`}
                              title="Salvează"
                              className="font-mono-label bg-black text-white border border-black p-2 hover:bg-white hover:text-black disabled:opacity-50"
                            >
                              <Check size={12} />
                            </button>
                            <button
                              onClick={cancelEdit}
                              disabled={saving}
                              data-testid={`sitemap-cancel-${g.id}-${i}`}
                              title="Anulează"
                              className="font-mono-label border border-black p-2 hover:bg-black hover:text-white"
                            >
                              <X size={12} />
                            </button>
                          </>
                        ) : (
                          <>
                            {r.editable && (
                              <button
                                onClick={() => startEdit(r)}
                                data-testid={`sitemap-edit-btn-${g.id}-${i}`}
                                title="Editează slug"
                                className="font-mono-label border border-black p-2 hover:bg-black hover:text-white"
                              >
                                <Pencil size={12} />
                              </button>
                            )}
                            <button
                              onClick={() => copy(full)}
                              data-testid={`sitemap-copy-${g.id}-${i}`}
                              title="Copiază URL"
                              className="font-mono-label border border-black p-2 hover:bg-black hover:text-white"
                            >
                              <Copy size={12} />
                            </button>
                            <a
                              href={r.path}
                              target="_blank"
                              rel="noreferrer"
                              title="Deschide"
                              className="font-mono-label border border-black p-2 hover:bg-black hover:text-white inline-flex items-center"
                            >
                              <ExternalLink size={12} />
                            </a>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      {!deptsLoaded && (
        <div className="mt-6 font-mono-label text-black/50" data-testid="sitemap-loading">
          Se încarcă departamentele...
        </div>
      )}
    </div>
  );
}
